/* eslint-disable max-len */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreatePromotionStepProps } from 'domains/promotion/Promotion.types';
import PromotionImageSelection from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/PromotionImageSelection';
import {
  getDiscountPromotionsData,
  getFlashPromotionsData,
} from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/PublishStep.types';
import { TableBanner } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/TableBanner';
import { TableDiscount } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/TableDiscount';
import { TableQuantityLimited } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/TableQuantityLimited';
import { TableTimeLimited } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/TableTimeLimited';
import { Flex, Radio, Text } from 'UI';

import { useResetScroll } from 'utils/hooks/useResetScroll';
import { getBannerPromotionsData } from './PublishStep.mapper';

export const PublishStep = ({ promotion, setPromotion }: CreatePromotionStepProps) => {
  const { t } = useTranslation();
  const [showAdditionalSection, setShowAdditionalSection] = useState(false);

  useResetScroll();

  const getPublicationItems = () => {
    switch (promotion.promotionType) {
      case 'DISCOUNT': {
        return <TableDiscount data={getDiscountPromotionsData(promotion, showAdditionalSection)} />;
      }
      case 'BANNER': {
        return <TableBanner data={getBannerPromotionsData(promotion)} />;
      }
      case 'PROMOTION_FLASH_TIME_LIMITED': {
        return <TableTimeLimited data={getFlashPromotionsData(promotion)} />;
      }
      case 'PROMOTION_FLASH_QUANTITY_LIMITED': {
        return <TableQuantityLimited data={getFlashPromotionsData(promotion)} />;
      }
      default: {
        return <></>;
      }
    }
  };

  const renderPromotionVisibility = () => {
    if (promotion.promotionType !== 'DISCOUNT') {
      return null;
    }
    return (
      <Flex direction={'column'} gap={15} data-cy={'promotion-visibility-section'}>
        <Text type="heading3">{t('promotion.create_new.visibility.title', 'Promotion visibility')}</Text>
        <Text type={'text'}>
          {t(
            'promotion.create_new.visibility.description',
            'Would you like to maximize exposure by displaying this promotion on the homepage?',
          )}
        </Text>
        <Radio
          onChange={() => {
            setPromotion({
              ...promotion,
              displayOnHomepage: true,
            });
          }}
          checked={promotion.displayOnHomepage}
          label={t('promotion.create_new.visibility.yes', 'Yes, feature this promotion on the homepage')}
          data-cy={'promotion-visibility-yes'}
        />
        <Radio
          onChange={() =>
            setPromotion({
              ...promotion,
              displayOnHomepage: false,
            })
          }
          checked={!promotion.displayOnHomepage}
          label={t('promotion.create_new.visibility.no', 'No, keep it off the homepage')}
          data-cy={'promotion-visibility-no'}
        />
      </Flex>
    );
  };

  return (
    <Flex direction={'column'} gap={24} data-cy={'publish-page'}>
      <Flex direction={'column'} size={0} data-cy={'publish-page-header'}>
        <Text type={'h4_paragraph_title'}>{t('backoffice.promotion.publish.title', 'Publish')}</Text>
        {promotion.promotionType !== 'PROMOTION_FLASH_QUANTITY_LIMITED' && (
          <Text type={'text'}>
            {t(
              'backoffice.promotion.publish.description',
              // eslint-disable-next-line max-len
              'Please, select an appropriate visual, where users can clearly identify the promoted purpose and make sure that the text will be accessible. Your publication should appear on the website within 24 hours.',
            )}
          </Text>
        )}
      </Flex>
      {promotion.promotionType !== 'PROMOTION_FLASH_QUANTITY_LIMITED' && (
        <PromotionImageSelection
          promotion={promotion}
          setPromotion={setPromotion}
          setShowAdditionalSection={setShowAdditionalSection}
          data-cy={'select-picture-page'}
        />
      )}
      {renderPromotionVisibility()}
      <Text type="heading3">{t('promotion.create_new.title.promotion_details', 'Promotion details')}</Text>
      {getPublicationItems()}
    </Flex>
  );
};
